import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, map, Observable, pairwise, shareReplay, Subscription, take} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-loon-onderdeel-winst',
  templateUrl: './loon-onderdeel-winst.component.html',
  styleUrls: ['./loon-onderdeel-winst.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class LoonOnderdeelWinstComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;
  public heeftWuo = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    loonAlsOnderdeelVanWinst: new FormControl<number | null>(null),
    loonheffingOverLoonAlsOnderdeelVanWinst: new FormControl<number | null>(null),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        this.heeftWuo = !!(entity.aangifte?.box1?.winstUitOnderneming?.ondernemingen.length ?? 0 > 0);
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: Aangifte): void {
    this.formGroup.reset({}, {emitEvent: false});
    this.formGroup.patchValue({loonAlsOnderdeelVanWinst: details.voorheffingen?.loonAlsOnderdeelVanWinst}, {emitEvent: false});
    this.formGroup.patchValue({loonheffingOverLoonAlsOnderdeelVanWinst: details.voorheffingen?.loonheffingOverLoonAlsOnderdeelVanWinst}, {emitEvent: false});
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      this.formGroup.controls.loonheffingOverLoonAlsOnderdeelVanWinst.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  saveForm(): void {
    const values = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        voorheffingen: {
          ...aangifte.voorheffingen,
          loonAlsOnderdeelVanWinst: values.loonAlsOnderdeelVanWinst ?? undefined,
          loonheffingOverLoonAlsOnderdeelVanWinst: values.loonheffingOverLoonAlsOnderdeelVanWinst ?? undefined,
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }

}
